/**
 * Analytics tab — time-series trends, campaign performance table,
 * attribution funnel, and cross-channel comparison.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui'
import { CampaignPerformanceTable } from '@/components/marketing/CampaignPerformanceTable'
import { CampaignSelectionChip } from '@/components/marketing/CampaignSelectionChip'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui'
import { RefreshCw } from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useMarketingStore } from '@/stores/marketingStore'
import { ChannelBadge } from '@/components/marketing/ChannelBadge'
import { useToast } from '@/components/ui/Toast'
import type { ChannelMetrics } from '@/types/marketing'
import { PerformanceTrends } from '@/components/marketing/visualizations/PerformanceTrends'
import type { TimeSeriesDataPoint } from '@/components/marketing/visualizations/PerformanceTrends'
import { AttributionFunnel } from '@/components/marketing/visualizations/AttributionFunnel'
import { formatMarketingCents } from '@/components/marketing/format'

const formatCurrency = formatMarketingCents

function formatNumber(n: number): string {
  return n.toLocaleString()
}

export function AnalyticsDashboard() {
  const {
    fetchStatus,
    fetchCampaignPerformance,
    fetchTimeSeries,
    fetchAttribution,
    fetchChannelComparison,
    syncInsights,
    hasAccount,
    campaignPerformance,
    timeSeries,
    attributionFunnel,
    attributionUnattributed,
  } = useMarketing()
  const selectedChannel = useMarketingStore((s) => s.selectedChannel)
  const selectedCampaignIds = useMarketingStore((s) => s.selectedCampaignIds)
  const clearSelectedCampaigns = useMarketingStore((s) => s.clearSelectedCampaigns)
  const { addToast } = useToast()

  // Full-page spinner for the INITIAL load only. Filter-driven reloads
  // (channel/date/campaign selection) keep content mounted and swap data in
  // place — tearing the tab down would unmount the performance table and
  // wipe its local filter/sort state on every row click.
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  // Shared with the dashboard toolbar — one date-range mental model across tabs
  const days = useMarketingStore((s) => s.dateRangeDays)
  const setDateRangeDays = useMarketingStore((s) => s.setDateRangeDays)
  const [channelComparison, setChannelComparison] = useState<{
    channels: ChannelMetrics[]
    blended: Record<string, number>
  } | null>(null)

  // Track whether we have an account (set once on mount, avoids re-render loop)
  const hasAccountRef = useRef(false)

  const loadAnalytics = useCallback(async (numDays: number) => {
    try {
      if (hasAccountRef.current) {
        const fetches: Promise<unknown>[] = [
          fetchCampaignPerformance(numDays),
          fetchTimeSeries(numDays),
          fetchAttribution(numDays),
        ]
        // Fetch channel comparison when multiple channels connected
        const channels = useMarketingStore.getState().connectedChannels
        const channel = useMarketingStore.getState().selectedChannel
        if (channels.length > 1 && !channel) {
          fetches.push(
            fetchChannelComparison(numDays).then((data: { channels: ChannelMetrics[]; blended: Record<string, number> }) => setChannelComparison(data))
          )
        }
        await Promise.all(fetches)
      }
    } catch {
      // silently handle
    } finally {
      setLoading(false)
    }
  }, [fetchCampaignPerformance, fetchTimeSeries, fetchAttribution, fetchChannelComparison])

  // Initial load: fetch status once, then load analytics
  useEffect(() => {
    let cancelled = false
    fetchStatus().then((status) => {
      if (cancelled) return
      hasAccountRef.current = status.hasAccount
      loadAnalytics(days)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload when the channel filter, shared date range, or campaign selection
  // changes (but not on initial mount)
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return }
    loadAnalytics(days)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, days, selectedCampaignIds])

  const handleSync = async () => {
    setSyncing(true)
    try {
      await syncInsights()
      addToast({ title: 'Synced', description: 'Insights synced from Meta', type: 'success' })
      await loadAnalytics(days)
    } catch {
      addToast({ title: 'Error', description: 'Failed to sync insights', type: 'error' })
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!hasAccount) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No Meta Ad Account connected. Go to <strong>Settings</strong> to connect.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={String(days)} onValueChange={(v) => setDateRangeDays(parseInt(v))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>

          {/* Cross-widget campaign selection (hidden when empty) */}
          <CampaignSelectionChip />
        </div>
        <Button variant="secondary" size="sm" onClick={handleSync} disabled={syncing}>
          {syncing ? <Spinner size="sm" className="mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Sync Insights
        </Button>
      </div>

      {/* Time Series — interactive Nivo line chart */}
      {timeSeries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Performance Trends</CardTitle>
            <CardDescription>Daily metrics for the last {days} days</CardDescription>
          </CardHeader>
          <CardContent>
            <PerformanceTrends
              data={timeSeries as unknown as TimeSeriesDataPoint[]}
              metrics={['spend', 'clicks', 'conversations']}
              height={280}
              yFormat="number"
              enableArea
            />
          </CardContent>
        </Card>
      )}

      {/* Campaign Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Performance</CardTitle>
          <CardDescription>Metrics per campaign</CardDescription>
        </CardHeader>
        <CardContent className="p-0 pb-4">
          <CampaignPerformanceTable rows={campaignPerformance} />
        </CardContent>
      </Card>

      {/* Attribution Funnel — interactive Nivo funnel */}
      {attributionFunnel.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Attribution Funnel</CardTitle>
            <CardDescription>Conversion pipeline</CardDescription>
          </CardHeader>
          <CardContent>
            <AttributionFunnel
              data={attributionFunnel.map((step) => ({
                id: step.label.toLowerCase().replace(/\s+/g, '_'),
                label: step.label,
                value: step.count,
                cost: step.cost,
                untracked: step.untracked,
              }))}
              height={320}
              // Steps carry no campaign identity — a click un-scopes an
              // active selection back to all campaigns.
              onStepClick={selectedCampaignIds.length > 0 ? clearSelectedCampaigns : undefined}
            />
            {attributionUnattributed != null && attributionUnattributed > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {attributionUnattributed.toLocaleString()} completed {attributionUnattributed === 1 ? 'order' : 'orders'} not attributed to a campaign
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Channel Comparison — shown when multiple channels connected */}
      {channelComparison && channelComparison.channels.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Channel Performance Comparison</CardTitle>
            <CardDescription>Side-by-side metrics across Meta and Google Ads</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  {channelComparison.channels.map((ch) => (
                    <TableHead key={ch.channel} className="text-right">
                      <ChannelBadge channel={ch.channel} size="md" />
                    </TableHead>
                  ))}
                  <TableHead className="text-right text-muted-foreground">Blended</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { key: 'spend', label: 'Spend', format: formatCurrency },
                  { key: 'impressions', label: 'Impressions', format: formatNumber },
                  { key: 'clicks', label: 'Clicks', format: formatNumber },
                  { key: 'ctr', label: 'CTR', format: (v: number) => `${(v / 100).toFixed(2)}%` },
                  { key: 'cpc', label: 'CPC', format: formatCurrency },
                  { key: 'conversions', label: 'Conversions', format: formatNumber },
                  { key: 'cpa', label: 'CPA', format: formatCurrency },
                  { key: 'revenue', label: 'Revenue', format: formatCurrency },
                  { key: 'roas', label: 'ROAS', format: (v: number) => `${v}x` },
                ].map(({ key, label, format }) => (
                  <TableRow key={key}>
                    <TableCell className="font-medium">{label}</TableCell>
                    {channelComparison.channels.map((ch) => (
                      <TableCell key={ch.channel} className="text-right tabular-nums">
                        {format((ch as unknown as Record<string, number>)[key] || 0)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right text-muted-foreground tabular-nums">
                      {format(channelComparison.blended[key] || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
